import { isShowMode } from "./showMode";

export type AnalyticsConsentDecision = "accepted" | "rejected";

export type AnalyticsConsentState = {
  version: string;
  decision: AnalyticsConsentDecision;
  timestamp: string;
  promptedDate: string;
};

declare global {
  interface Window {
    umami?: {
      track: (
        event:
          | string
          | {
              website?: string;
              url?: string;
              title?: string;
              name?: string;
              data?: Record<string, unknown>;
            },
        data?: Record<string, unknown>
      ) => void;
    };
  }
}

const CONSENT_STORAGE_KEY = "zeroco2.show.analyticsConsent";
const UMAMI_SCRIPT_ID = "zeroco2-umami-script";
export const ANALYTICS_CONSENT_VERSION = "2026-05-18";
export const ANALYTICS_CONSENT_CHANGED_EVENT = "zeroco2:analytics-consent-changed";
export const ANALYTICS_CONSENT_SETTINGS_EVENT = "zeroco2:analytics-consent-settings";

const UMAMI_ENABLED = String(import.meta.env.VITE_UMAMI_ENABLED || "").trim();
const UMAMI_WEBSITE_ID = String(import.meta.env.VITE_UMAMI_WEBSITE_ID || "").trim();
const UMAMI_SCRIPT_URL = String(import.meta.env.VITE_UMAMI_SCRIPT_URL || "").trim();
const UMAMI_HOST_URL = String(import.meta.env.VITE_UMAMI_HOST_URL || "").trim();

let scriptRequested = false;
let scriptLoaded = false;
const queuedEvents: string[] = [];

function analyticsConfigured() {
  return (
    isShowMode &&
    (UMAMI_ENABLED === "1" || UMAMI_ENABLED.toLowerCase() === "true") &&
    !!UMAMI_WEBSITE_ID &&
    !!UMAMI_SCRIPT_URL
  );
}

function notifyConsentChanged() {
  window.dispatchEvent(new Event(ANALYTICS_CONSENT_CHANGED_EVENT));
}

export function currentAnalyticsConsentDate() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseConsent(raw: string | null): AnalyticsConsentState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<AnalyticsConsentState>;
    if (
      parsed.version === ANALYTICS_CONSENT_VERSION &&
      (parsed.decision === "accepted" || parsed.decision === "rejected") &&
      typeof parsed.timestamp === "string"
    ) {
      return {
        version: parsed.version,
        decision: parsed.decision,
        timestamp: parsed.timestamp,
        promptedDate: typeof parsed.promptedDate === "string" ? parsed.promptedDate : ""
      };
    }
  } catch {
    return null;
  }
  return null;
}

export function getAnalyticsConsent(): AnalyticsConsentState | null {
  if (typeof window === "undefined") return null;
  return parseConsent(window.localStorage.getItem(CONSENT_STORAGE_KEY));
}

export function hasAnalyticsConsent() {
  const consent = getAnalyticsConsent();
  return consent?.decision === "accepted" && consent.promptedDate === currentAnalyticsConsentDate();
}

export function shouldShowAnalyticsConsentPrompt(consent = getAnalyticsConsent()) {
  return !consent || consent.promptedDate !== currentAnalyticsConsentDate();
}

export function analyticsAvailable() {
  return analyticsConfigured();
}

export function setAnalyticsConsent(decision: AnalyticsConsentDecision) {
  const state: AnalyticsConsentState = {
    version: ANALYTICS_CONSENT_VERSION,
    decision,
    timestamp: new Date().toISOString(),
    promptedDate: currentAnalyticsConsentDate()
  };
  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  notifyConsentChanged();
  if (decision === "accepted") {
    ensureAnalyticsLoaded();
  } else {
    unloadAnalytics();
  }
  trackAnalyticsEvent(decision === "accepted" ? "analytics_consent_accepted" : "analytics_consent_rejected");
}

export function resetAnalyticsConsent() {
  window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  unloadAnalytics();
  notifyConsentChanged();
}

export function openAnalyticsConsentSettings() {
  window.dispatchEvent(new Event(ANALYTICS_CONSENT_SETTINGS_EVENT));
}

export function currentAnalyticsUrl() {
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  const hash = window.location.hash || "#/";
  return `${path}${hash}`;
}

export function trackAnalyticsPageview() {
  if (!hasAnalyticsConsent()) return;
  ensureAnalyticsLoaded();
  if (!scriptLoaded || !window.umami) return;
  window.umami.track({
    website: UMAMI_WEBSITE_ID,
    url: currentAnalyticsUrl(),
    title: document.title || "ZeroCO2 Show"
  });
}

export function trackAnalyticsEvent(eventName: string) {
  if (!hasAnalyticsConsent()) return;
  ensureAnalyticsLoaded();
  if (!scriptLoaded || !window.umami) {
    queuedEvents.push(eventName);
    return;
  }
  window.umami.track(eventName);
}

export function ensureAnalyticsLoaded() {
  if (!analyticsConfigured() || !hasAnalyticsConsent() || scriptRequested) return;
  scriptRequested = true;

  const existingScript = document.getElementById(UMAMI_SCRIPT_ID);
  if (existingScript) existingScript.remove();

  const script = document.createElement("script");
  script.id = UMAMI_SCRIPT_ID;
  script.async = true;
  script.defer = true;
  script.src = UMAMI_SCRIPT_URL;
  script.dataset.websiteId = UMAMI_WEBSITE_ID;
  script.dataset.autoTrack = "false";
  script.dataset.excludeSearch = "true";
  script.dataset.domains = "www.zeroco2glass.de";
  if (UMAMI_HOST_URL) {
    script.dataset.hostUrl = UMAMI_HOST_URL;
  }
  script.addEventListener("load", () => {
    scriptLoaded = true;
    trackAnalyticsPageview();
    while (queuedEvents.length) {
      const eventName = queuedEvents.shift();
      if (eventName) window.umami?.track(eventName);
    }
  });
  document.head.appendChild(script);
}

function unloadAnalytics() {
  const script = document.getElementById(UMAMI_SCRIPT_ID);
  if (script) script.remove();
  scriptRequested = false;
  scriptLoaded = false;
  queuedEvents.length = 0;
  delete window.umami;
}
